import React, { useState } from 'react';
import { ArrowRight, Leaf, Recycle, Heart, Droplets, Wind, TreePine, Star, Quote, Truck, RefreshCw, Shield, CheckCircle, Instagram, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 8: REFORMATION
 * Style: Sustainable, Eco-friendly, Conscious Fashion
 * Best for: Organic clothing, Khadi, Handloom, Sustainable Indian brands
 * Light theme with natural green/earth accents
 */
const Template8 = () => {
    const navigate = useNavigate();
    const [activeImpact, setActiveImpact] = useState(0);

    // Sustainability impact stats
    const impactStats = [
        { icon: Droplets, value: '50L', label: 'Water Saved Per Garment', description: 'Compared to conventional cotton' },
        { icon: Leaf, value: '100%', label: 'Natural Fabrics', description: 'Organic & certified materials' },
        { icon: Recycle, value: '0', label: 'Plastic Packaging', description: 'Completely plastic-free' },
        { icon: TreePine, value: '1000+', label: 'Trees Planted', description: 'Through our green initiative' }
    ];

    // Sustainable categories
    const categories = [
        { name: 'Khadi Collection', description: 'Village-woven Excellence', image: 'https://placehold.co/600x800/ECFDF5/065F46?text=Khadi', tag: 'Handspun' },
        { name: 'Organic Cotton', description: 'Certified Pure', image: 'https://placehold.co/600x800/F0FDF4/166534?text=Cotton', tag: 'GOTS Certified' },
        { name: 'Natural Dyes', description: 'Plant-Based Colors', image: 'https://placehold.co/600x800/DCFCE7/15803D?text=Dyed', tag: 'Chemical-Free' },
        { name: 'Handloom Sarees', description: 'Artisan Crafted', image: 'https://placehold.co/600x800/D1FAE5/047857?text=Sarees', tag: 'Zero Carbon' }
    ];

    // Featured products
    const products = [
        { id: 1, name: 'Organic Khadi Kurta Set', price: '₹2,899', impact: '40L water saved', image: 'https://placehold.co/400x500/ECFDF5/065F46?text=Kurta+1', badge: 'Bestseller' },
        { id: 2, name: 'Hand-block Printed Dress', price: '₹3,499', impact: 'Natural dyes', image: 'https://placehold.co/400x500/F0FDF4/166534?text=Dress+1', badge: 'New' },
        { id: 3, name: 'Chanderi Handloom Saree', price: '₹5,999', impact: 'Zero carbon', image: 'https://placehold.co/400x500/DCFCE7/15803D?text=Saree+1', badge: 'Handwoven' },
        { id: 4, name: 'Organic Cotton Palazzo', price: '₹1,799', impact: '30L water saved', image: 'https://placehold.co/400x500/D1FAE5/047857?text=Palazzo+1', badge: 'Everyday' },
        { id: 5, name: 'Hemp Blend Jacket', price: '₹4,299', impact: 'Plastic-free', image: 'https://placehold.co/400x500/ECFDF5/065F46?text=Jacket+1', badge: 'Premium' },
        { id: 6, name: 'Bamboo Silk Kurti', price: '₹2,199', impact: 'Renewable fiber', image: 'https://placehold.co/400x500/F0FDF4/166534?text=Kurti+1', badge: 'Trending' }
    ];

    // Artisan stories
    const artisanStories = [
        {
            name: 'Kamala Devi',
            craft: 'Khadi Weaver',
            location: 'Wardha, Maharashtra',
            story: 'For three generations, my family has been spinning khadi. Each thread carries the spirit of swadeshi.',
            image: 'https://placehold.co/300x300/ECFDF5/065F46?text=Artisan+1'
        },
        {
            name: 'Rajan Bhai',
            craft: 'Block Printer',
            location: 'Bagru, Rajasthan',
            story: 'Our natural dyes come from indigo, pomegranate, and marigold. No chemicals touch our fabrics.',
            image: 'https://placehold.co/300x300/F0FDF4/166534?text=Artisan+2'
        },
        {
            name: 'Lakshmi Amma',
            craft: 'Handloom Expert',
            location: 'Kanchipuram, Tamil Nadu',
            story: 'Every saree I weave takes 15 days. Fast fashion cannot replicate this love and patience.',
            image: 'https://placehold.co/300x300/DCFCE7/15803D?text=Artisan+3'
        }
    ];

    // Testimonials
    const testimonials = [
        { text: 'Switching to sustainable fashion was the best decision. The organic cotton feels amazing on skin, and I feel good knowing I\'m supporting artisans.', name: 'Kavitha Nair', location: 'Kochi', rating: 5 },
        { text: 'The khadi kurtas are breathable even in Chennai summers. Plus, the transparency about sourcing builds real trust.', name: 'Arjun Krishnamurthy', location: 'Chennai', rating: 5 },
        { text: 'My daughter loved that her lehenga was made with zero plastic. This is the future of Indian fashion.', name: 'Sunita Joshi', location: 'Pune', rating: 5 }
    ];

    // Sustainability certifications
    const certifications = [
        { name: 'GOTS Certified', desc: 'Global Organic Textile Standard' },
        { name: 'Fair Trade', desc: 'Ethical sourcing practices' },
        { name: 'Carbon Neutral', desc: 'Offset shipping emissions' },
        { name: 'Vegan', desc: 'No animal products' }
    ];

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* Hero Section - Nature Inspired */}
            <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-teal-200/30 rounded-full blur-3xl" />

                {/* Floating Leaf Elements */}
                <div className="absolute top-20 left-[10%] opacity-20">
                    <Leaf className="w-12 h-12 text-emerald-600 transform rotate-45" />
                </div>
                <div className="absolute top-40 right-[15%] opacity-20">
                    <Leaf className="w-8 h-8 text-green-600 transform -rotate-12" />
                </div>
                <div className="absolute bottom-32 left-[20%] opacity-20">
                    <Leaf className="w-10 h-10 text-teal-600 transform rotate-90" />
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen py-16">
                        {/* Text Content */}
                        <div className="text-center lg:text-left">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full mb-6">
                                <Leaf className="w-4 h-4 text-emerald-600" />
                                <span className="text-emerald-700 text-sm font-medium">Sustainable Fashion</span>
                            </div>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light text-emerald-950 leading-[1.1] mb-6">
                                Fashion That
                                <span className="block text-emerald-600 font-medium">Gives Back</span>
                                To Earth
                            </h1>
                            <p className="text-emerald-800/70 text-lg lg:text-xl max-w-lg mx-auto lg:mx-0 mb-8 leading-relaxed">
                                Embrace conscious style with our collection of handwoven, organic,
                                and naturally dyed clothing. Made by Indian artisans, for a greener tomorrow.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <button
                                    onClick={() => navigate('/template8/shop')}
                                    className="group px-8 py-4 bg-emerald-700 text-white text-sm font-medium tracking-wider uppercase hover:bg-emerald-800 transition-all duration-300 rounded-full flex items-center justify-center gap-3"
                                >
                                    Shop Sustainable
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button className="px-8 py-4 border-2 border-emerald-300 text-emerald-700 text-sm font-medium tracking-wider uppercase hover:border-emerald-600 hover:bg-emerald-50 transition-all duration-300 rounded-full">
                                    Our Story
                                </button>
                            </div>

                            {/* Quick Impact Stats */}
                            <div className="mt-12 pt-8 border-t border-emerald-200">
                                <p className="text-emerald-700 text-sm font-medium mb-4">Our Impact So Far</p>
                                <div className="flex justify-center lg:justify-start gap-8">
                                    <div>
                                        <p className="text-3xl font-light text-emerald-700">50L+</p>
                                        <p className="text-sm text-emerald-600/70">Liters Water Saved</p>
                                    </div>
                                    <div>
                                        <p className="text-3xl font-light text-emerald-700">500+</p>
                                        <p className="text-sm text-emerald-600/70">Artisans Supported</p>
                                    </div>
                                    <div>
                                        <p className="text-3xl font-light text-emerald-700">0</p>
                                        <p className="text-sm text-emerald-600/70">Plastic Used</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Hero Image */}
                        <div className="relative">
                            <div className="relative aspect-[4/5] max-w-lg mx-auto">
                                <div className="absolute inset-0 bg-emerald-200 rounded-3xl transform rotate-3" />
                                <div className="relative z-10 overflow-hidden rounded-3xl">
                                    <img
                                        src="https://placehold.co/600x750/ECFDF5/065F46?text=Sustainable+Fashion"
                                        alt="Sustainable Fashion"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                {/* Impact Badge */}
                                <div className="absolute -left-4 bottom-20 bg-white shadow-xl px-5 py-4 rounded-xl z-20">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                                            <Recycle className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-emerald-600 font-medium">100% Sustainable</p>
                                            <p className="text-emerald-950 font-semibold">Zero Waste</p>
                                        </div>
                                    </div>
                                </div>
                                {/* Certification Badge */}
                                <div className="absolute -right-4 top-20 bg-white shadow-xl px-4 py-3 rounded-xl z-20">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                                        <span className="text-sm font-medium text-emerald-900">GOTS Certified</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Impact Stats Section */}
            <section className="py-16 lg:py-20 bg-emerald-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl lg:text-3xl font-light text-white">Every Purchase Makes a Difference</h2>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {impactStats.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="w-16 h-16 bg-emerald-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <stat.icon className="w-8 h-8 text-emerald-300" />
                                </div>
                                <p className="text-4xl font-light text-white mb-2">{stat.value}</p>
                                <p className="text-emerald-200 font-medium">{stat.label}</p>
                                <p className="text-emerald-400 text-sm mt-1">{stat.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Categories Section */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-emerald-600 text-sm font-medium tracking-[0.2em] uppercase">Shop By Values</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-emerald-950 mt-3">Conscious Collections</h2>
                        <p className="text-emerald-700/70 text-lg mt-4 max-w-2xl mx-auto">
                            Every collection is crafted with purpose, supporting traditional crafts and protecting our planet
                        </p>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {categories.map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template8/shop')}
                                className="group cursor-pointer relative overflow-hidden rounded-2xl"
                            >
                                <div className="aspect-[3/4] overflow-hidden">
                                    <img
                                        src={category.image}
                                        alt={category.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/70 via-emerald-900/20 to-transparent" />
                                <div className="absolute top-4 left-4">
                                    <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full">
                                        {category.tag}
                                    </span>
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6">
                                    <p className="text-emerald-200 text-xs uppercase tracking-wider mb-1">{category.description}</p>
                                    <h3 className="text-white text-lg lg:text-xl font-medium">{category.name}</h3>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Mission Statement */}
            <section className="py-20 lg:py-28 bg-gradient-to-br from-emerald-50 to-teal-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Leaf className="w-12 h-12 text-emerald-500 mx-auto mb-6" />
                    <h2 className="text-3xl lg:text-4xl font-light text-emerald-950 mb-6 leading-relaxed">
                        "We don't just make clothes. We nurture traditions, support communities,
                        and <span className="text-emerald-600 font-medium">heal the planet</span> — one garment at a time."
                    </h2>
                    <p className="text-emerald-700/70 text-lg max-w-2xl mx-auto">
                        Inspired by Gandhi's vision of swadeshi and self-reliance, we bring you fashion
                        that honors both the maker and Mother Earth.
                    </p>
                </div>
            </section>

            {/* Featured Products */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 lg:mb-16">
                        <div>
                            <span className="text-emerald-600 text-sm font-medium tracking-[0.2em] uppercase">New Arrivals</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-emerald-950 mt-3">Eco-Friendly Favorites</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template8/shop')}
                            className="group flex items-center gap-2 text-emerald-700 font-medium mt-4 lg:mt-0 hover:text-emerald-900 transition-colors"
                        >
                            View All Products
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template8/shop')}
                                className="group cursor-pointer"
                            >
                                <div className="relative aspect-[4/5] overflow-hidden bg-emerald-50 rounded-2xl mb-4">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <span className="absolute top-3 left-3 px-3 py-1 bg-emerald-600 text-white text-xs font-medium rounded-full">
                                        {product.badge}
                                    </span>
                                    <button className="absolute top-3 right-3 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white">
                                        <Heart className="w-4 h-4 text-emerald-700" />
                                    </button>
                                    {/* Impact Label */}
                                    <div className="absolute bottom-3 left-3 right-3 px-3 py-2 bg-white/95 rounded-lg flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                        <Leaf className="w-4 h-4 text-emerald-600" />
                                        <span className="text-xs text-emerald-700 font-medium">{product.impact}</span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <h3 className="text-emerald-950 font-medium group-hover:text-emerald-600 transition-colors line-clamp-1">
                                        {product.name}
                                    </h3>
                                    <p className="text-emerald-800 font-semibold">{product.price}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Artisan Stories */}
            <section className="py-16 lg:py-24 bg-emerald-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-emerald-600 text-sm font-medium tracking-[0.2em] uppercase">Behind The Craft</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-emerald-950 mt-3">Meet Our Artisans</h2>
                        <p className="text-emerald-700/70 text-lg mt-4 max-w-2xl mx-auto">
                            The hands that weave magic into every thread. Their skill, their story, their legacy.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {artisanStories.map((artisan, index) => (
                            <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300">
                                <div className="aspect-square overflow-hidden">
                                    <img
                                        src={artisan.image}
                                        alt={artisan.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="p-6">
                                    <p className="text-emerald-600 text-sm font-medium">{artisan.craft}</p>
                                    <h3 className="text-emerald-950 text-xl font-medium mt-1">{artisan.name}</h3>
                                    <p className="text-emerald-600/70 text-sm mt-1">{artisan.location}</p>
                                    <p className="text-emerald-700/80 mt-4 leading-relaxed italic">"{artisan.story}"</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Transparency Section */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <div>
                            <span className="text-emerald-600 text-sm font-medium tracking-[0.2em] uppercase">Full Transparency</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-emerald-950 mt-3 mb-6">
                                Know Where Your Clothes Come From
                            </h2>
                            <p className="text-emerald-700/80 text-lg leading-relaxed mb-8">
                                We believe you deserve to know the journey of every garment — from field to fabric to finished product.
                                Scan our QR codes to trace your purchase back to its origin.
                            </p>
                            <div className="space-y-4 mb-8">
                                {[
                                    'Farm-to-closet traceability',
                                    'Fair wages for every artisan',
                                    'Chemical-free processing',
                                    'Carbon-neutral shipping'
                                ].map((item, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                                        <span className="text-emerald-800">{item}</span>
                                    </div>
                                ))}
                            </div>
                            <button
                                onClick={() => navigate('/template8/shop')}
                                className="group inline-flex items-center gap-3 px-8 py-4 bg-emerald-700 text-white text-sm font-medium tracking-wider uppercase hover:bg-emerald-800 transition-all duration-300 rounded-full"
                            >
                                Learn Our Process
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-4">
                                <div className="aspect-[4/5] overflow-hidden rounded-2xl">
                                    <img
                                        src="https://placehold.co/400x500/ECFDF5/065F46?text=Farm"
                                        alt="Organic Farm"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="aspect-square overflow-hidden rounded-2xl">
                                    <img
                                        src="https://placehold.co/300x300/F0FDF4/166534?text=Spinning"
                                        alt="Spinning"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                            <div className="space-y-4 pt-8">
                                <div className="aspect-square overflow-hidden rounded-2xl">
                                    <img
                                        src="https://placehold.co/300x300/DCFCE7/15803D?text=Weaving"
                                        alt="Weaving"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="aspect-[4/5] overflow-hidden rounded-2xl">
                                    <img
                                        src="https://placehold.co/400x500/D1FAE5/047857?text=Dyeing"
                                        alt="Natural Dyeing"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-16 lg:py-24 bg-gradient-to-br from-emerald-50 to-teal-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-emerald-600 text-sm font-medium tracking-[0.2em] uppercase">Customer Love</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-emerald-950 mt-3">Conscious Choices, Happy Hearts</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow duration-300">
                                <div className="flex gap-1 mb-4">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 fill-emerald-500 text-emerald-500" />
                                    ))}
                                </div>
                                <p className="text-emerald-800/80 leading-relaxed mb-6">
                                    "{testimonial.text}"
                                </p>
                                <div className="flex items-center gap-3 pt-4 border-t border-emerald-100">
                                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                                        <span className="text-emerald-700 font-medium">{testimonial.name[0]}</span>
                                    </div>
                                    <div>
                                        <p className="font-medium text-emerald-950">{testimonial.name}</p>
                                        <p className="text-emerald-600/70 text-sm">{testimonial.location}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Certifications */}
            <section className="py-12 bg-white border-y border-emerald-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-16">
                        {certifications.map((cert, index) => (
                            <div key={index} className="text-center">
                                <p className="text-emerald-900 font-medium">{cert.name}</p>
                                <p className="text-emerald-600/70 text-sm">{cert.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Newsletter */}
            <section className="py-16 lg:py-24 bg-emerald-900 text-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Leaf className="w-10 h-10 text-emerald-400 mx-auto mb-6" />
                    <h2 className="text-3xl lg:text-4xl font-light text-white mb-4">
                        Join the Green Revolution
                    </h2>
                    <p className="text-emerald-200 text-lg mb-8">
                        Subscribe for sustainable fashion tips, artisan stories, and exclusive eco-friendly offers.
                        Get 15% off your first conscious purchase!
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-4 bg-emerald-800 border border-emerald-700 rounded-full text-white placeholder-emerald-400 focus:outline-none focus:border-emerald-500 transition-colors"
                        />
                        <button className="px-8 py-4 bg-emerald-500 text-emerald-950 text-sm font-semibold tracking-wider uppercase hover:bg-emerald-400 transition-colors rounded-full whitespace-nowrap">
                            Subscribe
                        </button>
                    </div>
                    <p className="text-emerald-400 text-sm mt-4">
                        No spam. Just good vibes and green choices.
                    </p>
                </div>
            </section>

            {/* Instagram */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <Instagram className="w-6 h-6 text-emerald-700" />
                            <span className="text-emerald-700 font-medium">@reformationfashion</span>
                        </div>
                        <h2 className="text-3xl lg:text-4xl font-light text-emerald-950">#SustainableStyle</h2>
                    </div>

                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 lg:gap-4">
                        {[1, 2, 3, 4, 5, 6].map((_, index) => (
                            <div key={index} className="group relative aspect-square overflow-hidden rounded-xl cursor-pointer">
                                <img
                                    src={`https://placehold.co/300x300/ECFDF5/065F46?text=Eco+${index + 1}`}
                                    alt={`Instagram post ${index + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-emerald-700/0 group-hover:bg-emerald-700/30 transition-colors duration-300 flex items-center justify-center">
                                    <Heart className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services */}
            <section className="py-12 bg-emerald-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Truck className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                                <p className="font-medium text-emerald-950">Carbon-Neutral Shipping</p>
                                <p className="text-emerald-600/70 text-sm">Free above ₹999</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <RefreshCw className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                                <p className="font-medium text-emerald-950">Easy Returns</p>
                                <p className="text-emerald-600/70 text-sm">15-day return policy</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Shield className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                                <p className="font-medium text-emerald-950">Certified Organic</p>
                                <p className="text-emerald-600/70 text-sm">GOTS approved</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Leaf className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                                <p className="font-medium text-emerald-950">Plastic-Free</p>
                                <p className="text-emerald-600/70 text-sm">Eco packaging only</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template8;
